import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';

interface LinkInfo {
  name: string;
  maxFileSize: number;
  allowedFileTypes: string[];
  requirePassword: boolean;
  expiresAt: Date | null;
  isActive: boolean;
  uploadsReceived: number;
  maxUploads: number | null;
}

interface UploadStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'active' | 'completed' | 'error';
  timing?: string;
}

interface UploadFile {
  file: File;
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  uploadedSize: number;
}

const PublicUploadPage: React.FC = () => {
  const { shortCode } = useParams<{ shortCode: string }>();
  const [linkInfo, setLinkInfo] = useState<LinkInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [uploadSteps, setUploadSteps] = useState<UploadStep[]>([]);
  const [showSteps, setShowSteps] = useState(false);
  const [uploaderName, setUploaderName] = useState('');
  const [uploaderEmail, setUploaderEmail] = useState('');
  const [uploadComplete, setUploadComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch link information
  useEffect(() => {
    fetchLinkInfo();
  }, [shortCode]);

  const fetchLinkInfo = async () => {
    try {
      const response = await fetch(`http://localhost:8787/api/public/upload/${shortCode}`);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Invalid upload link');
        setLoading(false);
        return;
      }

      setLinkInfo(data.link);
      setAuthenticated(!data.link.requirePassword);
    } catch (error) {
      setError('Failed to load upload page');
      console.error('Error fetching link info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Authenticate with password
  const authenticatePassword = async () => {
    try {
      const response = await fetch(`http://localhost:8787/api/public/upload/${shortCode}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        setAuthenticated(true);
      } else {
        alert('Incorrect password');
      }
    } catch (error) {
      alert('Authentication failed');
    }
  };

  // Initialize upload steps
  const initializeUploadSteps = (fileName: string, fileSize: number): UploadStep[] => [
    {
      id: 'validate',
      title: 'Validate File',
      description: `Checking: ${fileName} (${(fileSize / 1024 / 1024).toFixed(1)}MB) against limits`,
      status: 'pending',
      timing: '<1ms',
    },
    {
      id: 'prepare',
      title: 'Prepare Upload',
      description: 'Getting upload URL from server, creating file metadata',
      status: 'pending',
      timing: '20-50ms',
    },
    {
      id: 'upload',
      title: 'Upload to Storage',
      description: `Uploading to CloudFlare R2 storage at /uploads/${shortCode}/`,
      status: 'pending',
      timing: 'Depends on file size and connection',
    },
    {
      id: 'verify',
      title: 'Verify Upload',
      description: 'Confirming file integrity and updating link statistics',
      status: 'pending',
      timing: '10-30ms',
    },
    {
      id: 'notify',
      title: 'Send Notification',
      description: 'Notifying link owner of new upload',
      status: 'pending',
      timing: '50-100ms',
    },
  ];

  // Update step status
  const updateStep = (stepId: string, status: UploadStep['status']) => {
    setUploadSteps(prev => prev.map(step =>
      step.id === stepId ? { ...step, status } : step
    ));
  };

  // Handle file upload
  const uploadFile = async (file: File, uploadId: string) => {
    if (!linkInfo) return;

    setShowSteps(true);
    const steps = initializeUploadSteps(file.name, file.size);
    setUploadSteps(steps);

    try {
      // Step 1: Validate file
      updateStep('validate', 'active');
      await new Promise(resolve => setTimeout(resolve, 10));

      // Check file size
      if (file.size > linkInfo.maxFileSize) {
        updateStep('validate', 'error');
        throw new Error(`File too large. Max size: ${linkInfo.maxFileSize / 1024 / 1024}MB`);
      }

      // Check file type if restrictions exist
      if (linkInfo.allowedFileTypes.length > 0 && !linkInfo.allowedFileTypes.includes('*')) {
        const fileExt = file.name.split('.').pop()?.toLowerCase();
        if (!fileExt || !linkInfo.allowedFileTypes.includes(fileExt)) {
          updateStep('validate', 'error');
          throw new Error(`File type not allowed. Accepted: ${linkInfo.allowedFileTypes.join(', ')}`);
        }
      }

      updateStep('validate', 'completed');

      // Step 2: Prepare upload
      updateStep('prepare', 'active');

      const prepareResponse = await fetch(`http://localhost:8787/api/public/upload/${shortCode}/prepare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          uploaderName,
          uploaderEmail,
        }),
      });

      if (!prepareResponse.ok) {
        updateStep('prepare', 'error');
        throw new Error('Failed to prepare upload');
      }

      const { uploadUrl, uploadId: serverUploadId } = await prepareResponse.json();
      updateStep('prepare', 'completed');

      // Step 3: Upload file
      updateStep('upload', 'active');

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('file', file);

      // Track upload progress
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          setUploadFiles(prev => prev.map(f =>
            f.id === uploadId ? {
              ...f,
              progress,
              uploadedSize: event.loaded,
            } : f
          ));
        }
      });

      // Wrap XHR in promise
      await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));

        xhr.open('PUT', uploadUrl);
        xhr.send(file);
      });

      updateStep('upload', 'completed');

      // Step 4: Verify upload
      updateStep('verify', 'active');

      const verifyResponse = await fetch(`http://localhost:8787/api/public/upload/${shortCode}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uploadId: serverUploadId,
          fileName: file.name,
        }),
      });

      if (!verifyResponse.ok) {
        updateStep('verify', 'error');
        throw new Error('Failed to verify upload');
      }

      updateStep('verify', 'completed');

      // Step 5: Send notification
      updateStep('notify', 'active');
      await new Promise(resolve => setTimeout(resolve, 50)); // Simulated
      updateStep('notify', 'completed');

      // Mark file as completed
      setUploadFiles(prev => prev.map(f =>
        f.id === uploadId ? { ...f, status: 'completed', progress: 100 } : f
      ));

      // Show completion after last file
      const allCompleted = uploadFiles.every(f => f.id === uploadId || f.status === 'completed');
      if (allCompleted) {
        setTimeout(() => setUploadComplete(true), 1000);
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadFiles(prev => prev.map(f =>
        f.id === uploadId ? {
          ...f,
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        } : f
      ));

      // Mark current step as error if not already marked
      const currentStep = uploadSteps.find(s => s.status === 'active');
      if (currentStep) {
        updateStep(currentStep.id, 'error');
      }
    }
  };

  // Handle file drop
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (!linkInfo || !authenticated) return;

    const newFiles: UploadFile[] = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substring(7),
      progress: 0,
      status: 'pending',
      uploadedSize: 0,
    }));

    setUploadFiles(prev => [...prev, ...newFiles]);

    // Start uploading each file
    newFiles.forEach(fileItem => {
      uploadFile(fileItem.file, fileItem.id);
    });
  }, [linkInfo, authenticated]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: !authenticated || !linkInfo?.isActive,
  });

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-xl">Loading upload page...</div>
      </div>
    );
  }

  // Error state
  if (error || !linkInfo) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold mb-2">Invalid Upload Link</h1>
          <p className="text-gray-400">{error || 'This link does not exist or has expired.'}</p>
        </div>
      </div>
    );
  }

  // Link expired or inactive
  if (!linkInfo.isActive || (linkInfo.expiresAt && new Date(linkInfo.expiresAt) < new Date())) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⏰</div>
          <h1 className="text-2xl font-bold mb-2">Link Expired</h1>
          <p className="text-gray-400">This upload link is no longer active.</p>
        </div>
      </div>
    );
  }

  // Max uploads reached
  if (linkInfo.maxUploads && linkInfo.uploadsReceived >= linkInfo.maxUploads) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📊</div>
          <h1 className="text-2xl font-bold mb-2">Upload Limit Reached</h1>
          <p className="text-gray-400">This link has reached its maximum number of uploads.</p>
        </div>
      </div>
    );
  }

  // Password protection
  if (linkInfo.requirePassword && !authenticated) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold mb-4">🔒 Password Required</h1>
          <p className="text-gray-400 mb-6">This upload link is password protected.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700 mb-4"
            onKeyPress={(e) => e.key === 'Enter' && authenticatePassword()}
          />
          <button
            onClick={authenticatePassword}
            className="w-full py-2 bg-blue-600 rounded-lg font-bold hover:bg-blue-700"
          >
            Authenticate
          </button>
        </div>
      </div>
    );
  }

  // Upload complete
  if (uploadComplete) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-3xl font-bold mb-2">Upload Complete!</h1>
          <p className="text-gray-400 mb-6">Your files have been successfully uploaded.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 rounded-lg font-bold hover:bg-blue-700"
          >
            Upload More Files
          </button>
        </motion.div>
      </div>
    );
  }

  // Main upload interface
  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold mb-2">📤 Upload Files</h1>
          <p className="text-gray-400">{linkInfo.name}</p>
          <div className="flex gap-4 mt-4 text-sm text-gray-500">
            <span>Max file size: {linkInfo.maxFileSize / 1024 / 1024}MB</span>
            {linkInfo.maxUploads && (
              <span>Uploads remaining: {linkInfo.maxUploads - linkInfo.uploadsReceived}</span>
            )}
          </div>
        </motion.div>

        {/* Optional uploader info */}
        {uploadFiles.length === 0 && (
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-bold mb-4">Your Information (Optional)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                value={uploaderName}
                onChange={(e) => setUploaderName(e.target.value)}
                placeholder="Your name"
                className="px-4 py-2 bg-gray-900 rounded-lg border border-gray-700"
              />
              <input
                type="email"
                value={uploaderEmail}
                onChange={(e) => setUploaderEmail(e.target.value)}
                placeholder="Your email"
                className="px-4 py-2 bg-gray-900 rounded-lg border border-gray-700"
              />
            </div>
          </div>
        )}

        {/* Drop zone */}
        <div
          {...getRootProps()}
          className={`bg-gray-800 rounded-lg p-12 text-center cursor-pointer transition border-2 border-dashed ${
            isDragActive ? 'border-blue-500 bg-blue-900/20' : 'border-gray-600 hover:border-gray-500'
          }`}
        >
          <input {...getInputProps()} />
          <div className="text-6xl mb-4">📁</div>
          {isDragActive ? (
            <p className="text-xl">Drop files here...</p>
          ) : (
            <>
              <p className="text-xl mb-2">Drag & drop files here</p>
              <p className="text-gray-400">or click to select files</p>
            </>
          )}
        </div>

        {/* Upload progress */}
        {uploadFiles.length > 0 && (
          <div className="mt-6 space-y-3">
            <h3 className="text-lg font-bold">Uploading Files:</h3>
            {uploadFiles.map((file) => (
              <div key={file.id} className="bg-gray-800 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">{file.file.name}</span>
                  <span className="text-sm text-gray-400">
                    {(file.file.size / 1024 / 1024).toFixed(1)} MB
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      file.status === 'error' ? 'bg-red-500' :
                      file.status === 'completed' ? 'bg-green-500' :
                      'bg-blue-500'
                    }`}
                    style={{ width: `${file.progress}%` }}
                  />
                </div>
                {file.status === 'error' && (
                  <p className="text-red-400 text-sm mt-2">{file.error}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Upload steps visualization */}
        <AnimatePresence>
          {showSteps && uploadSteps.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 bg-gray-800 rounded-lg p-6"
            >
              <h3 className="text-lg font-bold mb-4">Upload Process:</h3>
              <div className="space-y-3">
                {uploadSteps.map((step, index) => (
                  <motion.div
                    key={step.id}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex items-start gap-3 p-3 rounded-lg ${
                      step.status === 'completed' ? 'bg-green-900/20' :
                      step.status === 'active' ? 'bg-blue-900/20' :
                      step.status === 'error' ? 'bg-red-900/20' :
                      'bg-gray-700/20'
                    }`}
                  >
                    <div className={`mt-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      step.status === 'completed' ? 'bg-green-500' :
                      step.status === 'active' ? 'bg-blue-500 animate-pulse' :
                      step.status === 'error' ? 'bg-red-500' :
                      'bg-gray-600'
                    }`}>
                      {step.status === 'completed' ? '✓' :
                       step.status === 'error' ? '✗' :
                       step.status === 'active' ? '•' : ''}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">{step.title}</div>
                      <div className="text-sm text-gray-400">{step.description}</div>
                      {step.timing && (
                        <div className="text-xs text-gray-500 mt-1">Time: {step.timing}</div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PublicUploadPage;